import { Button, Dialog } from '@artosera/ui';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ContactPresente } from '../../api/contacts.js';
import { estApiError, estErreurReseau } from '../../api/errors.js';
import { useCreerFacture } from '../../api/ventes.js';
import { RechercheContact } from './RechercheContact.js';
import styles from './NouvelleFactureDialog.module.css';

export interface NouvelleFactureDialogProps {
  galerieSlug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreee: (id: string) => void;
  /**
   * Œuvre à facturer, quand le dialogue s'ouvre depuis sa fiche : le brouillon
   * naît avec la ligne de cette œuvre (désignation, prix, TVA repris de la
   * fiche par l'API). L'œuvre ne passe « vendue » qu'à l'émission, pas ici.
   */
  oeuvre?: { id: string; titre: string; artisteNom?: string } | undefined;
}

/**
 * Dialogue de création (3.3, écran 5) : contact du carnet par recherche, puis
 * ouverture du brouillon (§ 17.4).
 *
 * L'API exige au moins une ligne à la création
 * (`apps/api/src/ventes/factures.routes.ts`, `lignes.min(1)`) : une facture
 * sans ligne n'a pas de sens fiscal. Le dialogue pose donc une ligne libre à
 * zéro, que la fiche invite aussitôt à compléter — c'est le geste le plus
 * court vers un brouillon qui reste entièrement modifiable.
 */
export function NouvelleFactureDialog({
  galerieSlug,
  open,
  onOpenChange,
  onCreee,
  oeuvre,
}: NouvelleFactureDialogProps): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const [contact, setContact] = useState<ContactPresente | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const creer = useCreerFacture(galerieSlug);

  async function soumettre(): Promise<void> {
    if (!contact) return;
    setErreur(null);
    try {
      const reponse = await creer.mutateAsync({
        contactId: contact.id,
        lignes: oeuvre
          ? [{ oeuvreId: oeuvre.id }]
          : [{ designation: t('regie:ventes.nouvelle.lignePlaceholder'), prixUnitaireCentimes: 0 }],
      });
      onOpenChange(false);
      onCreee(reponse.facture.id);
    } catch (error) {
      if (estApiError(error)) setErreur(error.message);
      else if (estErreurReseau(error)) setErreur(t('commun:erreurReseau'));
      else setErreur(t('commun:erreurReseau'));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(ouvert) => {
        onOpenChange(ouvert);
        if (!ouvert) {
          setContact(null);
          setErreur(null);
        }
      }}
      title={t('regie:ventes.nouvelle.titre')}
      description={
        oeuvre
          ? t('regie:ventes.nouvelle.descriptionOeuvre', {
              oeuvre: [oeuvre.artisteNom, oeuvre.titre].filter(Boolean).join(' — '),
            })
          : t('regie:ventes.nouvelle.description')
      }
      closeLabel={t('commun:actions.fermer')}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('commun:actions.annuler')}
          </Button>
          <Button
            onClick={() => void soumettre()}
            loading={creer.isPending}
            disabled={!contact || creer.isPending}
          >
            {t('regie:ventes.nouvelle.creer')}
          </Button>
        </>
      }
    >
      <div className={styles.formulaire}>
        {/* Le brouillon à venir, en miniature : le contact choisi s'y inscrit
            à la place du destinataire. Redondant pour un lecteur d'écran. */}
        <div className={styles.miniature} aria-hidden="true" data-rempli={contact ? 'oui' : 'non'}>
          <span className={styles.miniatureEntete} />
          <span className={styles.miniatureDestinataire}>
            {contact ? contact.nomAffiche : null}
          </span>
          <span className={styles.miniatureLignes} />
          <span className={styles.miniatureTotal} />
        </div>
        <div className={styles.champ}>
          <RechercheContact
            galerieSlug={galerieSlug}
            contact={contact}
            onChoisir={setContact}
            onEffacer={() => setContact(null)}
          />
          {erreur ? (
            <p className={styles.erreur} role="alert">
              {erreur}
            </p>
          ) : null}
        </div>
      </div>
    </Dialog>
  );
}
